/**
 * Générateur — Programmer la construction d'un chemin simple
 * Attendu ID : 690 | Niveau : 6ème | Domaine : Algorithmique (initiation à la programmation)
 *
 * Types générés (1 catégorie, 4 formats) :
 *   - chemin_complet_drag     : drag — assembler la séquence complète du couloir (flèches en trop)
 *   - programme_labyrinthe_qcm: QCM 4 choix — quel programme atteint la cible sans mur ni sortie ?
 *   - fin_de_chemin_qcm       : QCM 4 choix — quelle suite termine le chemin commencé ?
 *   - obstacle_vf             : V/F 4 lignes — analyse d'un programme candidat dans le décor
 *
 * Langage commun aux gens 687-690 : grille 5×5, flèches ↑ ↓ ← →, robot sur la
 * case « D ». Décor labyrinthe : cases noires (murs), case cible orange.
 *
 * UNICITÉ DE LA SOLUTION (contrainte du correcteur automatique) : le décor est
 * construit en COULOIR — un chemin auto-évitant SANS CORDE (cf. estSansCorde,
 * revue 2026-08-13) est tiré, puis toutes les cases voisines du chemin qui
 * n'en font pas partie deviennent des murs. Le
 * seul trajet possible de « D » à la cible est donc le chemin canonique :
 * le drag et la « suite du chemin » ont une réponse unique par construction,
 * et tout programme différent traverse un mur, sort de la grille ou s'arrête
 * ailleurs (leurres vérifiés par exécution).
 *
 * Frontière d'attendu : on CONSTRUIT (ou complète) un chemin sous contraintes
 * de décor — pas de boucle (689), pas de simple exécution (688), pas de
 * reconnaissance hors décor (687).
 *
 * Sources d'inspiration (recherche 2026-08-11) : ac-Bordeaux « déplacement
 * dans un labyrinthe » (6e), Juliette Hernando « Le chevalier / Le gruyère /
 * La clé » (parcours imposé, premier déplacement donné), eduscol.
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 690;

const L_DRAG = 'Programmer le chemin complet (glisser-déposer)';
const L_PROG = 'Choisir le programme qui atteint la cible (QCM)';
const L_FIN  = 'Terminer le chemin commencé (QCM)';
const L_VF   = 'Analyser un programme dans le labyrinthe (V/F)';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Moteur de déplacements (partagé avec les gens 687-689) ──

const TAILLE = 5;

const DIRS = {
    '↑': { dx: 0, dy: -1 },
    '↓': { dx: 0, dy: 1 },
    '←': { dx: -1, dy: 0 },
    '→': { dx: 1, dy: 0 },
};
const FLECHES = Object.keys(DIRS);
const OPPOSE = { '↑': '↓', '↓': '↑', '←': '→', '→': '←' };

const cle = (c) => `${c.x},${c.y}`;

const LIGNES_NOMS = ['A', 'B', 'C', 'D', 'E'];
const nomCase = (c) => `${LIGNES_NOMS[c.y]}${c.x + 1}`;

const executer = (depart, prog) => {
    const cells = [{ ...depart }];
    let { x, y } = depart;
    for (const f of prog) {
        x += DIRS[f].dx;
        y += DIRS[f].dy;
        if (x < 0 || y < 0 || x >= TAILLE || y >= TAILLE) return null;
        cells.push({ x, y });
    }
    return cells;
};

/** Exécution dans le décor : s'arrête si mur ou sortie. Retourne { cells, violation }. */
const executerAvecMurs = (depart, prog, mursSet) => {
    const cells = [{ ...depart }];
    let { x, y } = depart;
    for (const f of prog) {
        x += DIRS[f].dx;
        y += DIRS[f].dy;
        if (x < 0 || y < 0 || x >= TAILLE || y >= TAILLE) return { cells, violation: 'sortie' };
        if (mursSet.has(`${x},${y}`)) return { cells, violation: 'mur' };
        cells.push({ x, y });
    }
    return { cells, violation: null };
};

const estAutoEvitant = (cells) =>
    cells !== null && new Set(cells.map(cle)).size === cells.length;

/**
 * Chemin SANS CORDE (revue 2026-08-13) : deux cases non consécutives du
 * chemin ne sont jamais adjacentes dans la grille. Sans cette contrainte,
 * le couloir avait des raccourcis internes : 61,8 % des labyrinthes
 * admettaient plusieurs séquences valides de même longueur — l'élève
 * pouvait produire un chemin légal et être compté faux (A1).
 */
const estSansCorde = (cells) => {
    for (let i = 0; i < cells.length; i++) {
        for (let j = i + 2; j < cells.length; j++) {
            if (Math.abs(cells[i].x - cells[j].x) + Math.abs(cells[i].y - cells[j].y) === 1) return false;
        }
    }
    return true;
};

const tirerCheminAutoEvitant = (longueur) => {
    for (let essai = 0; essai < 200; essai++) {
        const depart = { x: randInt(0, TAILLE - 1), y: randInt(0, TAILLE - 1) };
        const prog = [];
        for (let i = 0; i < longueur; i++) {
            const interdites = i > 0 ? [OPPOSE[prog[i - 1]]] : [];
            prog.push(randomChoice(FLECHES.filter(f => !interdites.includes(f))));
        }
        const cells = executer(depart, prog);
        if (estAutoEvitant(cells) && estSansCorde(cells)) return { depart, prog, cells };
    }
    const depart = { x: 0, y: TAILLE - 1 };
    const prog = ['→', '↑', '→', '↑', '→'].slice(0, longueur);
    return { depart, prog, cells: executer(depart, prog) };
};

/**
 * Labyrinthe en couloir : chemin auto-évitant + murs sur TOUTES les cases
 * voisines (4-adjacence) du chemin qui n'en font pas partie. Le chemin
 * canonique est alors l'unique trajet de « D » à la cible.
 */
const tirerLabyrinthe = (longueur) => {
    const { depart, prog, cells } = tirerCheminAutoEvitant(longueur);
    const surChemin = new Set(cells.map(cle));
    const murs = [];
    const mursSet = new Set();
    cells.forEach((c) => {
        [[0, -1], [0, 1], [-1, 0], [1, 0]].forEach(([dx, dy]) => {
            const v = { x: c.x + dx, y: c.y + dy };
            if (v.x < 0 || v.y < 0 || v.x >= TAILLE || v.y >= TAILLE) return;
            const k = cle(v);
            if (surChemin.has(k) || mursSet.has(k)) return;
            mursSet.add(k);
            murs.push(v);
        });
    });
    return { depart, prog, cells, cible: cells[cells.length - 1], murs, mursSet };
};

/** Mutation simple d'un programme (une instruction remplacée). */
const muterProg = (prog) => {
    const copie = [...prog];
    const i = randInt(0, prog.length - 1);
    copie[i] = randomChoice(FLECHES.filter(f => f !== copie[i]));
    return copie;
};

// ─── Rendu SVG ───────────────────────────────────────────────

const CASE = 26;
const BLEU = '#1E3A8A';
const VERT = '#1D9E75';
const ORANGE = '#B45309';
const MUR = '#334155';

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

const enteteSvg = (w, h, aria) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="${aria}">`;

/** Labyrinthe : murs noirs, cible orange, départ « D », chemin partiel optionnel. */
const svgLabyrinthe = (ox, oy, laby, options = {}) => {
    const w = TAILLE * CASE;
    const parts = [];
    laby.murs.forEach((m) => {
        parts.push(`<rect x="${ox + m.x * CASE}" y="${oy + m.y * CASE}" width="${CASE}" height="${CASE}" fill="${MUR}"/>`);
    });
    for (let i = 0; i <= TAILLE; i++) {
        parts.push(`<line x1="${ox + i * CASE}" y1="${oy}" x2="${ox + i * CASE}" y2="${oy + w}" stroke="#cbd5e1" stroke-width="1"/>`);
        parts.push(`<line x1="${ox}" y1="${oy + i * CASE}" x2="${ox + w}" y2="${oy + i * CASE}" stroke="#cbd5e1" stroke-width="1"/>`);
    }
    parts.push(`<rect x="${ox}" y="${oy}" width="${w}" height="${w}" fill="none" stroke="${BLEU}" stroke-width="1.4"/>`);
    if (options.axes) {
        for (let i = 0; i < TAILLE; i++) {
            parts.push(`<text x="${ox - 9}" y="${oy + i * CASE + CASE / 2 + 4}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${LIGNES_NOMS[i]}</text>`);
            parts.push(`<text x="${ox + i * CASE + CASE / 2}" y="${oy - 6}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`);
        }
    }
    const centre = (c) => ({ cx: ox + c.x * CASE + CASE / 2, cy: oy + c.y * CASE + CASE / 2 });
    // cible
    const t = centre(laby.cible);
    parts.push(`<rect x="${t.cx - 10}" y="${t.cy - 10}" width="20" height="20" rx="3" fill="${ORANGE}" opacity="0.3"/>`);
    parts.push(`<circle cx="${t.cx}" cy="${t.cy}" r="5" fill="none" stroke="${ORANGE}" stroke-width="2"/>`);
    parts.push(`<circle cx="${t.cx}" cy="${t.cy}" r="1.8" fill="${ORANGE}"/>`);
    // chemin partiel tracé (fin_de_chemin)
    if (options.cheminPartiel && options.cheminPartiel.length >= 2) {
        const pts = options.cheminPartiel.map(c => { const p = centre(c); return `${p.cx},${p.cy}`; }).join(' ');
        const fin = centre(options.cheminPartiel[options.cheminPartiel.length - 1]);
        const avant = centre(options.cheminPartiel[options.cheminPartiel.length - 2]);
        const vx = Math.sign(fin.cx - avant.cx), vy = Math.sign(fin.cy - avant.cy);
        parts.push(`<polyline points="${pts}" fill="none" stroke="${VERT}" stroke-width="2.4"/>`);
        parts.push(`<path d="M${fin.cx + vx * 6},${fin.cy + vy * 6} L${fin.cx - vy * 4 - vx * 2},${fin.cy - vx * 4 - vy * 2} L${fin.cx + vy * 4 - vx * 2},${fin.cy + vx * 4 - vy * 2} Z" fill="${VERT}"/>`);
    }
    const dep = centre(laby.cells[0]);
    parts.push(`<circle cx="${dep.cx}" cy="${dep.cy}" r="8" fill="${BLEU}"/>`);
    parts.push(`<text x="${dep.cx}" y="${dep.cy + 3.5}" font-size="10" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">D</text>`);
    return parts.join('');
};

const svgProgramme = (ox, oy, prog) => {
    return prog.map((f, i) => {
        const x = ox + i * 30;
        return `<rect x="${x}" y="${oy}" width="22" height="22" rx="4" fill="#EFF6FF" stroke="${BLEU}" stroke-width="1.2"/>` +
            `<text x="${x + 11}" y="${oy + 16}" font-size="14" font-weight="700" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${f}</text>` +
            `<text x="${x + 11}" y="${oy + 36}" font-size="9" fill="#64748b" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`;
    }).join('');
};

const progEnTexte = (prog) => prog.join(' ');

// Signature d'anti-répétition (revue 2026-08-13, recette gen 687) : le texte
// de ces formats est fixe, le contenu variable vit dans le SVG.
const signatureDe = (payload) =>
    (payload.elements_additionnels?.elements?.[0]?.config?.imageUrl || '') + payload.texte;

const ARIA_DECOR = 'Un labyrinthe sur grille : cases noires infranchissables, case départ D en bleu, case cible entourée en orange';

// ─── Format 1 — Chemin complet (drag) ────────────────────────

let derniereSignatureDrag690 = null;

const genCheminCompletDrag = () => {
    let payload = construireCheminCompletDrag();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureDrag690; t++) {
        payload = construireCheminCompletDrag();
    }
    derniereSignatureDrag690 = signatureDe(payload);
    return payload;
};

const construireCheminCompletDrag = () => {
    const laby = tirerLabyrinthe(randInt(5, 6));

    const phrase = laby.prog.map(f => `{{${f}}}`).join(' ');
    const distracteurs = [randomChoice(FLECHES), randomChoice(FLECHES)];

    const largeurGrille = TAILLE * CASE;
    const svg = enteteSvg(largeurGrille + 20, largeurGrille + 20, ARIA_DECOR)
        + svgLabyrinthe(10, 10, laby)
        + '</svg>';

    const texte = `<p>Le robot part de la case « D » et doit atteindre la case cible (cercle orange) sans jamais passer par une case noire ni sortir de la grille. Chaque flèche le déplace d'une case. Programme son chemin en plaçant les flèches dans l'ordre (attention, certaines flèches proposées sont en trop).</p>`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DRAG),
        mode: 'drag',
        distracteurs,
    });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'chemin_complet_drag';
    return payload;
};

// ─── Format 2 — Quel programme atteint la cible ? (QCM) ──────

let derniereSignatureProg690 = null;

const genProgrammeLabyrintheQcm = () => {
    let payload = construireProgrammeLabyrintheQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureProg690; t++) {
        payload = construireProgrammeLabyrintheQcm();
    }
    derniereSignatureProg690 = signatureDe(payload);
    return payload;
};

const construireProgrammeLabyrintheQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const laby = tirerLabyrinthe(randInt(4, 5));
        const cleCible = cle(laby.cible);

        // Leurres : mutations (simples ou doubles) du bon programme, TOUS de
        // même longueur (revue 2026-08-13) — toute variation de longueur
        // rendait le comptage des flèches quasi gagnant (le plus court était
        // faux dans 97 % des cas). Chaque candidat est re-vérifié par
        // exécution : aucun n'atteint la cible proprement.
        const vus = new Set([laby.prog.join('')]);
        const leurres = [];
        for (let e2 = 0; e2 < 150 && leurres.length < 3; e2++) {
            const candidat = Math.random() < 0.5
                ? muterProg(laby.prog)
                : muterProg(muterProg(laby.prog));
            const k = candidat.join('');
            if (vus.has(k)) continue;
            const res = executerAvecMurs(laby.depart, candidat, laby.mursSet);
            const atteint = res.violation === null
                && cle(res.cells[res.cells.length - 1]) === cleCible;
            if (atteint) continue;
            vus.add(k);
            leurres.push(candidat);
        }
        if (leurres.length < 3) continue;

        const largeurGrille = TAILLE * CASE;
        const svg = enteteSvg(largeurGrille + 20, largeurGrille + 20, ARIA_DECOR)
            + svgLabyrinthe(10, 10, laby)
            + '</svg>';

        const bonne = progEnTexte(laby.prog);
        const choix = shuffle([bonne, ...leurres.map(progEnTexte)]);
        const texte = `<p>Le robot part de la case « D » et doit atteindre la case cible (cercle orange) sans jamais passer par une case noire ni sortir de la grille. Quel programme construit un chemin qui y arrive ?</p>`;

        const payload = makeQcm(choix, bonne, texte, opts(L_PROG));
        payload.elements_additionnels = svgToElement(svg);
        payload._meta.generatorKey = 'programme_labyrinthe_qcm';
        return payload;
    }
    return construireProgrammeLabyrintheQcm();
};

// ─── Format 3 — Terminer le chemin (QCM) ─────────────────────

let derniereSignatureFin690 = null;

const genFinDeCheminQcm = () => {
    let payload = construireFinDeCheminQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureFin690; t++) {
        payload = construireFinDeCheminQcm();
    }
    derniereSignatureFin690 = signatureDe(payload);
    return payload;
};

const construireFinDeCheminQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const laby = tirerLabyrinthe(6);
        const nbDonnees = randInt(3, 4);
        const debut = laby.prog.slice(0, nbDonnees);
        const suite = laby.prog.slice(nbDonnees); // 2 ou 3 instructions

        // Leurres : mutations de la suite, distinctes, qui ne terminent pas le
        // chemin proprement depuis la fin du début tracé.
        const departSuite = laby.cells[nbDonnees];
        const cleCible = cle(laby.cible);
        const vus = new Set([suite.join('')]);
        const leurres = [];
        for (let e2 = 0; e2 < 100 && leurres.length < 3; e2++) {
            const candidat = muterProg(suite);
            const k = candidat.join('');
            if (vus.has(k)) continue;
            const res = executerAvecMurs(departSuite, candidat, laby.mursSet);
            const atteint = res.violation === null
                && cle(res.cells[res.cells.length - 1]) === cleCible;
            if (atteint) continue;
            vus.add(k);
            leurres.push(candidat);
        }
        if (leurres.length < 3) continue;

        const largeurGrille = TAILLE * CASE;
        const svg = enteteSvg(largeurGrille + 20, largeurGrille + 20,
            `${ARIA_DECOR}. Le début du chemin est déjà tracé en vert`)
            + svgLabyrinthe(10, 10, laby, { cheminPartiel: laby.cells.slice(0, nbDonnees + 1) })
            + '</svg>';

        const bonne = progEnTexte(suite);
        const choix = shuffle([bonne, ...leurres.map(progEnTexte)]);
        const texte = `<p>Le robot part de la case « D » et doit atteindre la case cible (cercle orange) sans jamais passer par une case noire ni sortir de la grille. Le début de son chemin est déjà tracé en vert : ${progEnTexte(debut)}. Quelle suite d'instructions termine le chemin jusqu'à la cible ?</p>`;

        const payload = makeQcm(choix, bonne, texte, opts(L_FIN));
        payload.elements_additionnels = svgToElement(svg);
        payload._meta.generatorKey = 'fin_de_chemin_qcm';
        return payload;
    }
    return construireFinDeCheminQcm();
};

// ─── Format 4 — Analyser un programme (V/F) ──────────────────

let derniereSignatureVF690 = null;

const genObstacleVf = () => {
    let payload = construireObstacleVf();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureVF690; t++) {
        payload = construireObstacleVf();
    }
    derniereSignatureVF690 = signatureDe(payload);
    return payload;
};

// Chaque builder retourne { l, v, cases } ou null. `res` = exécution avec
// murs (cells = cases réellement parcourues, jusqu'au blocage éventuel),
// `atteint` = arrive exactement sur la cible. `g` = groupe d'exclusion
// (revue 2026-08-13) : les trois lignes d'issue décrivent le même dénouement
// et se donnaient mutuellement — une seule par tirage.
const BUILDERS_VF_690 = [
    {
        k: 'mur',
        g: 'issue',
        b: (ctx, vrai) => {
            const heurte = ctx.res.violation === 'mur';
            const l = (heurte === vrai)
                ? 'Ce programme fait entrer le robot dans une case noire.'
                : 'Ce programme ne fait jamais entrer le robot dans une case noire.';
            return { l, v: vrai, cases: [] };
        },
    },
    {
        k: 'sortie',
        g: 'issue',
        b: (ctx, vrai) => {
            const sort = ctx.res.violation === 'sortie';
            const l = (sort === vrai)
                ? 'Ce programme fait sortir le robot de la grille.'
                : 'Ce programme garde le robot dans la grille du début à la fin.';
            return { l, v: vrai, cases: [] };
        },
    },
    {
        k: 'cible',
        g: 'issue',
        b: (ctx, vrai) => {
            const l = (ctx.atteint === vrai)
                ? 'À la fin du programme, le robot est sur la case cible.'
                : 'À la fin du programme, le robot n\'est pas sur la case cible.';
            return { l, v: vrai, cases: [] };
        },
    },
    {
        k: 'nb_instr',
        g: 'compte',
        b: (ctx, vrai) => {
            const reel = ctx.prog.length;
            const nb = vrai ? reel : reel + randomChoice([-1, 1]);
            return { l: `Ce programme contient ${nb} instructions.`, v: vrai, cases: [] };
        },
    },
    {
        k: 'etape_k',
        g: 'etape',
        b: (ctx, vrai) => {
            // k strictement avant l'éventuel blocage : la position est décidable.
            if (ctx.res.cells.length < 2) return null;
            const k = randInt(1, ctx.res.cells.length - 1);
            const reelle = ctx.res.cells[k];
            let c = reelle;
            if (!vrai) {
                const autres = [];
                for (let x = 0; x < TAILLE; x++) {
                    for (let y = 0; y < TAILLE; y++) {
                        if ((x !== reelle.x || y !== reelle.y) && !ctx.mursSet.has(`${x},${y}`)) autres.push({ x, y });
                    }
                }
                c = randomChoice(autres);
            }
            return { l: `Après l'instruction n°${k}, le robot est sur la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
    {
        k: 'passe_par',
        g: 'passage',
        b: (ctx, vrai) => {
            const parcourues = new Set(ctx.res.cells.map(cle));
            const dep = ctx.res.cells[0];
            const candidates = [];
            for (let x = 0; x < TAILLE; x++) {
                for (let y = 0; y < TAILLE; y++) {
                    const k = `${x},${y}`;
                    const estDepart = x === dep.x && y === dep.y;
                    if (vrai && parcourues.has(k) && !estDepart) candidates.push({ x, y });
                    if (!vrai && !parcourues.has(k) && !ctx.mursSet.has(k)) candidates.push({ x, y });
                }
            }
            if (!candidates.length) return null;
            const c = randomChoice(candidates);
            return { l: `Au cours de son trajet, le robot passe par la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
];

const construireObstacleVf = () => {
    const laby = tirerLabyrinthe(randInt(4, 5));
    // Programme candidat : le bon chemin (1 fois sur 2) ou une mutation SANS
    // filtre (revue 2026-08-13) : le filtre « reste dans la grille » rendait
    // le fait « sortie » constant — la formulation seule donnait la réponse.
    const prog = Math.random() < 0.5 ? laby.prog : muterProg(laby.prog);
    const res = executerAvecMurs(laby.depart, prog, laby.mursSet);
    const atteint = res.violation === null
        && cle(res.cells[res.cells.length - 1]) === cle(laby.cible);
    const ctx = { prog, res, atteint, mursSet: laby.mursSet };

    const largeurGrille = TAILLE * CASE;
    const progLargeur = prog.length * 30 - 8;
    const w = Math.max(largeurGrille + 40, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 90,
        `${ARIA_DECOR}. Un programme numéroté en blocs flèches est affiché au-dessus, la grille porte des coordonnées lignes A à E et colonnes 1 à 5`)
        + svgProgramme((w - progLargeur) / 2, 10, prog)
        + svgLabyrinthe((w - largeurGrille) / 2 + 8, 68, laby, { axes: true })
        + '</svg>';

    const texte = `<p>Le robot part de la case « D ». La case cible est entourée en orange, les cases noires sont des murs infranchissables. On étudie le programme ci-dessus. Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;

    for (let essai = 0; essai < 60; essai++) {
        const nbV = randInt(1, 3);
        const camps = shuffle([...Array(4)].map((_, i) => i < nbV));
        // Un builder par groupe d'exclusion (revue 2026-08-13).
        const parGroupe = {};
        shuffle([...BUILDERS_VF_690]).forEach((bd) => {
            if (!parGroupe[bd.g]) parGroupe[bd.g] = bd;
        });
        const builders = shuffle(Object.values(parGroupe));
        if (builders.length !== 4) continue;
        const items = builders.map((bd, i) => bd.b(ctx, camps[i]));
        if (items.some(x => x === null)) continue;
        // Cases nommées deux à deux distinctes.
        const casesNommees = items.flatMap(x => x.cases);
        if (new Set(casesNommees).size !== casesNommees.length) continue;
        const lignes = items.map(x => x.l);
        if (new Set(lignes).size !== 4) continue;
        const bonnesReponses = items.map(x => (x.v ? 0 : 1));
        const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
            ...opts(L_VF),
            elements_additionnels: svgToElement(svg),
        });
        payload._meta.generatorKey = 'obstacle_vf';
        return payload;
    }
    return construireObstacleVf();
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    chemin_complet_drag:      genCheminCompletDrag,
    programme_labyrinthe_qcm: genProgrammeLabyrintheQcm,
    fin_de_chemin_qcm:        genFinDeCheminQcm,
    obstacle_vf:              genObstacleVf,
};

export const CATEGORIES = [
    {
        id: 'programmer_chemin',
        label: 'Programmer la construction d\'un chemin simple',
        formatsList: [
            { key: 'chemin_complet_drag',      label: L_DRAG },
            { key: 'programme_labyrinthe_qcm', label: L_PROG },
            { key: 'fin_de_chemin_qcm',        label: L_FIN },
            { key: 'obstacle_vf',              label: L_VF },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    TAILLE,
    executer,
    executerAvecMurs,
    tirerLabyrinthe,
    muterProg,
    BUILDERS_VF_690,
    genCheminCompletDrag,
    genProgrammeLabyrintheQcm,
    genFinDeCheminQcm,
    genObstacleVf,
};
